import { lazy } from 'react';
import { Navigate } from 'react-router';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import Unauthorised from 'views/unauthorised/Unauthorised';
import PrivateRoute from './utils/PrivateRoute';
import userTypes from 'constants/userTypes';
import { CORE_PERMISSIONS } from '@a1/shared';
import SentryErrorBoundary from '../SentryErrorBoundary';

// Dashboard
const DashboardBookingStats = Loadable(lazy(() => import('views/shared/dashboard-booking-stats')));
const DashboardSurveyStats = Loadable(lazy(() => import('views/shared/dashboard-survey-stats')));

// pages
const ViewAllRespondents = Loadable(lazy(() => import('views/manager/view-all-respondents')));
const Surveys = Loadable(lazy(() => import('views/manager/view-surveys')));
const Survey = Loadable(lazy(() => import('views/manager/view-survey')));
const ViewRespondent = Loadable(lazy(() => import('views/shared/respondent')));
const CreateRespondent = Loadable(lazy(() => import('views/shared/respondent/create-respondent')));
const EditRespondent = Loadable(lazy(() => import('views/shared/respondent/edit-respondent')));
const AnswerRespondentSurvey = Loadable(lazy(() => import('views/shared/answer-respondent-survey')));
const ViewRespondentSurvey = Loadable(lazy(() => import('views/shared/view-respondent-survey')));

// Manage Booking Pages
const ManageBookingPages = Loadable(lazy(() => import('views/manager/manage-booking-pages')));
const CreateBookingFromBookingPage = Loadable(lazy(() => import('views/manager/manage-booking-pages/create-booking')));
// Manage Bookings
const ManageBookings = Loadable(lazy(() => import('views/manager/manage-bookings')));
// Manage Bookings - Calendar
const ManageBookingCalendar = Loadable(lazy(() => import('views/manager/manage-booking-calendar')));

// full screen dialogs
const EnterExistingSurvey = Loadable(lazy(() => import('views/shared/enter-existing-survey')));
const IntroScreen = Loadable(lazy(() => import('views/shared/intro-screen')));
const FinishSurvey = Loadable(lazy(() => import('views/manager/finish-survey')));
const ExitSurvey = Loadable(lazy(() => import('views/manager/exit-survey')));

// ==============================|| MANAGER ROUTING ||============================== //

const ManagerRoutes = {
  path: '/',
  element: <MainLayout />,
  errorElement: <SentryErrorBoundary />,
  children: [
    {
      path: '/',
      element: <Navigate to="/respondents" />
    },
    {
      path: '/dashboard-booking-stats',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.VIEW_BOOKING_DASHBOARD}>
          <DashboardBookingStats />
        </PrivateRoute>
      )
    },
    {
      path: '/dashboard-survey-stats',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.VIEW_SURVEY_DASHBOARD}>
          <DashboardSurveyStats />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.RESPONDENT_LIST}>
          <ViewAllRespondents />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/create-respondent',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.RESPONDENT_WRITE} restrictedTo={[userTypes.Manager]}>
          <CreateRespondent />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/edit-respondent/:respondentId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.RESPONDENT_WRITE} restrictedTo={[userTypes.Manager]}>
          <EditRespondent />
        </PrivateRoute>
      )
    },
    {
      path: '/surveys',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.SURVEY_READ}>
          <Surveys />
        </PrivateRoute>
      )
    },
    {
      path: '/view-survey/:surveyId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.SURVEY_READ}>
          <Survey />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/view-respondent/:respondentId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.RESPONDENT_READ}>
          <ViewRespondent />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/view-respondent/:respondentId/booking/:bookingId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.RESPONDENT_READ}>
          <ViewRespondent />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/answer-respondent-survey/intro-screen/:surveyId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.SURVEY_WRITE}>
          <EnterExistingSurvey />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/answer-respondent-survey/intro-screen/:surveyId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.SURVEY_WRITE}>
          <IntroScreen />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/answer-respondent-survey/:surveyId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.SURVEY_WRITE}>
          <AnswerRespondentSurvey />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/answer-respondent-survey/finish-survey/:surveyId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.SURVEY_WRITE}>
          <FinishSurvey />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/answer-respondent-survey/exit-survey/:surveyId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.SURVEY_WRITE}>
          <ExitSurvey />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/view-respondent-survey/:surveyId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.SURVEY_READ}>
          <ViewRespondentSurvey />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-booking-pages',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.BOOKING_CREATE_VIA_BOOKING_PAGE}>
          <ManageBookingPages />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-bookings',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.BOOKING_READ}>
          <ManageBookings />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-bookings/:createdBookingId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.BOOKING_READ}>
          <ManageBookings />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-booking-pages/booking-page/:bookingPageUid/',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.BOOKING_CREATE_VIA_BOOKING_PAGE}>
          <CreateBookingFromBookingPage />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-booking-calendar',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.BOOKING_CALENDAR_READ}>
          <ManageBookingCalendar />
        </PrivateRoute>
      )
    },
    {
      path: '/forbidden',
      element: <Unauthorised />
    }
  ]
};

export default ManagerRoutes;
